import React, { FC, useEffect, useLayoutEffect } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { Router, useHistory } from 'react-router-dom';
import { createBrowserHistory } from 'history';

import { CssBaseline } from '@material-ui/core';
import { MuiThemeProvider, StylesProvider } from '@material-ui/core/styles';

import { CognitoUserProvider, GlobalClientProvider } from 'providers';
import { Loaders, Notify, ScrollToTop } from 'components';
import { useSyncBillingCustomerMutation } from 'api';
import { isRoot } from 'utilities';
import { useNotify } from 'hooks';
import * as paths from 'paths';
import storage, { LOGOUT_COOKIE } from 'storage';

import i18n from 'config/i18n';
import theme from 'theme';

import Protected from './protected/ProtectedIndex';
import Public from './public/PublicIndex';
import RootProtectedRoutes from './root/protected/Routes';
import RootPublicRoutes from './root/public/Routes';
import ReactQueryProvider from './ReactQueryProvider';
import TokenCheck from 'features/auth/containers/TokenCheck';
import LegacyProviderCheck from 'features/auth/LegacyProviderCheck';

const ALLOWED_REFERRERS = [
  'billing.stripe.com',
  'stripe.com',
  'www.stripe.com',
  'checkout.stripe.com',
];

/**
 * The core of the app is on the subdomain
 * The protected and public folders are for subdomains
 */
const Subdomain: FC = () => {
  const history = useHistory();
  const notify = useNotify();
  const { t } = useTranslation();
  const { error, mutate, isLoading } = useSyncBillingCustomerMutation({
    onError: () => {
      notify.error({ message: t('errors:billingSyncError') });
    },
  });

  useEffect(() => {
    const { referrer } = window.document;
    const urlParams = new URLSearchParams(window.location.search);
    const ssoError = urlParams.get('error_description');

    if (ssoError) {
      history.push(paths.authError);
    }

    if (referrer) {
      const url = new URL(referrer);
      const fromBilling = ALLOWED_REFERRERS.includes(url.host);
      fromBilling && mutate({});
    }

    window?.analytics?.page();
    history.listen(() => window?.analytics?.page());
  }, []);

  if (isLoading || error) {
    return <Loaders.Startup />;
  }

  return (
    <GlobalClientProvider>
      <TokenCheck>
        <CognitoUserProvider>
          {cognitoState => (cognitoState ? <Protected /> : <Public />)}
        </CognitoUserProvider>
      </TokenCheck>
    </GlobalClientProvider>
  );
};

/**
 * Root domain is for client lookup and workspace selection
 * It is for the user while the subdomain is for the client instance
 */
const RootDomain: FC = () => {
  const history = useHistory();

  useLayoutEffect(() => {
    if (storage.logout) {
      const nextUrl = storage.logout;
      storage.remove(LOGOUT_COOKIE);
      window.location.replace(nextUrl);
    }
  }, []);

  useEffect(() => {
    window?.analytics?.page();
    history.listen(() => window?.analytics?.page());
  }, []);

  return (
    <TokenCheck>
      <LegacyProviderCheck>
        <CognitoUserProvider>
          {cognitoState => (cognitoState ? <RootProtectedRoutes /> : <RootPublicRoutes />)}
        </CognitoUserProvider>
      </LegacyProviderCheck>
    </TokenCheck>
  );
};

const App: FC = () => (
  <StylesProvider>
    <MuiThemeProvider theme={theme}>
      <ReactQueryProvider>
        <CssBaseline />
        <I18nextProvider i18n={i18n}>
          <Router history={createBrowserHistory()}>
            <ScrollToTop />
            <Notify />
            {isRoot() ? <RootDomain /> : <Subdomain />}
          </Router>
        </I18nextProvider>
      </ReactQueryProvider>
    </MuiThemeProvider>
  </StylesProvider>
);

export default App;
